import { useEffect, useState } from "react";
import { hasSeenIntro } from "../state/scroll";

/**
 * Full-screen luxury loading veil shown until the Earth globe (the only
 * thing visible the instant the veil lifts) is confirmed ready. We expose
 * a tiny global signal so independent modules can mark themselves done
 * without a context plumbing exercise:
 *
 *   window.__readySignals.earth = true   // set from Earth.jsx after textures
 *   window.__readySignals.map   = true   // set from MapPhase.jsx on load
 *
 * The map signal is tracked but no longer gates the veil: MapPhase isn't
 * shown until well after the intro finishes playing and the user scrolls
 * into it, and its map tiles/style stream from a remote server whose
 * timing is out of our hands. Blocking the FIRST paint of the page on a
 * network fetch for something the user won't see for several more seconds
 * was the main reason this loader felt slow — it waited on the slower of
 * two independent loads even though only one of them was actually needed
 * up front. The map keeps loading quietly in the background regardless.
 *
 * Once earth is ready (or after MAX_WAIT ms as a safety net) we fade out.
 */

const MAX_WAIT_MS = 3200;

if (typeof window !== "undefined" && !window.__readySignals) {
  window.__readySignals = { earth: false, map: false };
}

export default function LoadingOverlay() {
  // On return SPA navigations, assets are already in memory — skip the overlay.
  const isReturnVisit = hasSeenIntro();

  const [done, setDone] = useState(isReturnVisit);
  const [ready, setReady] = useState(isReturnVisit);
  const [hidden, setHidden] = useState(isReturnVisit);
  const [progress, setProgress] = useState(isReturnVisit ? 1 : 0);

  useEffect(() => {
    if (isReturnVisit) return; // Assets cached — skip the loader
    let alive = true;
    const start = performance.now();
    let rafId;

    const tick = () => {
      if (!alive) return;
      const sig = window.__readySignals || {};
      const elapsed = performance.now() - start;

      const timeFrac = Math.min(elapsed / MAX_WAIT_MS, 0.9);
      const signalBoost = sig.earth ? 0.6 : 0;
      const target = Math.min(0.9, timeFrac * 0.4 + signalBoost);

      setProgress((prev) => (target > prev ? target : prev));

      if (sig.earth || elapsed > MAX_WAIT_MS) {
        setProgress(1);
        setReady(true);
        return;
      }
      rafId = requestAnimationFrame(tick);
    };

    rafId = requestAnimationFrame(tick);
    return () => { alive = false; cancelAnimationFrame(rafId); };
  }, []);

  // Let the bar visibly reach 100% and settle for a beat before the veil
  // lifts — avoids the "bar still filling while the globe already spins" feel.
  useEffect(() => {
    if (!ready || done) return;
    const t = setTimeout(() => setDone(true), 360);
    return () => clearTimeout(t);
  }, [ready, done]);

  // When the veil begins to lift, tell the globe to start its intro spin.
  useEffect(() => {
    if (!done) return;
    window.dispatchEvent(new CustomEvent("loader-complete"));
  }, [done]);

  useEffect(() => {
    if (!done) return;
    const t = setTimeout(() => setHidden(true), 800);
    return () => clearTimeout(t);
  }, [done]);

  if (hidden) return null;

  return (
    <div
      className="loading-overlay"
      data-done={done ? "true" : "false"}
      aria-hidden={done ? "true" : "false"}
    >
      <style>{`
        .loading-overlay {
          position: fixed; inset: 0; z-index: 9999;
          background: radial-gradient(ellipse at center, #0b1322 0%, #04110a 70%, #02030a 100%);
          display: flex; align-items: center; justify-content: center;
          flex-direction: column;
          opacity: 1;
          transition: opacity 700ms ease;
          pointer-events: auto;
          padding: 24px;
          text-align: center;
        }
        .loading-overlay[data-done="true"] {
          opacity: 0;
          pointer-events: none;
        }
        .loading-logo {
          width: clamp(200px, 32vw, 300px);
          height: auto;
          display: block;
          margin: 0 auto 8px;
          filter: drop-shadow(0 4px 16px rgba(0, 0, 0, 0.5));
          opacity: 0;
          animation: loading-logo-in 1100ms cubic-bezier(0.16, 1, 0.3, 1) both;
        }
        @keyframes loading-logo-in {
          0%   { opacity: 0; transform: translateY(8px) scale(0.96); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
        @media (max-width: 380px) {
          .loading-logo { width: 96px; margin-bottom: 20px; }
        }
        .loading-mark {
          font-family: 'Montserrat', system-ui, sans-serif;
          font-weight: 300;
          font-size: clamp(1.5rem, 6vw, 3.2rem);
          letter-spacing: 0.18em;
          color: #f4f0e4;
          text-transform: uppercase;
          max-width: 100%;
        }
        @media (max-width: 380px) {
          .loading-mark { letter-spacing: 0.10em; }
        }
        .loading-mark em {
          font-style: italic;
          background: linear-gradient(120deg, #d4a45c, #eccb92, #d4a45c);
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent;
        }
        .loading-rule {
          width: 80px; height: 1px; margin: 22px 0;
          background: linear-gradient(90deg, transparent, #d4a45c, transparent);
        }
        .loading-sub {
          font-family: 'Inter', system-ui, sans-serif;
          color: rgba(244,240,228,0.55);
          font-size: 10px;
          letter-spacing: 0.5em;
          text-transform: uppercase;
          max-width: 100%;
        }
        @media (max-width: 380px) {
          .loading-sub { letter-spacing: 0.28em; font-size: 9px; }
        }
        .loading-bar {
          margin-top: 38px;
          width: clamp(220px, 28vw, 340px);
          height: 2px;
          background: rgba(244,240,228,0.10);
          border-radius: 2px;
          overflow: hidden;
          position: relative;
        }
        .loading-bar__fill {
          position: absolute; inset: 0 auto 0 0;
          width: 0%;
          background: linear-gradient(90deg, rgba(212, 164, 92,0) 0%, #d4a45c 30%, #eccb92 50%, #d4a45c 70%, rgba(212, 164, 92,0) 100%);
          background-size: 200% 100%;
          box-shadow: 0 0 12px rgba(212, 164, 92,0.55);
          transition: width 360ms ease-out;
          animation: loading-bar-shimmer 2.4s linear infinite;
        }
        @keyframes loading-bar-shimmer {
          0%   { background-position: 0% 50%; }
          100% { background-position: 200% 50%; }
        }
      `}</style>
      <img
        src="/brand/logo-full.png"
        alt="Nausiddhi Realty"
        className="loading-logo"
        width="260"
        height="204"
        decoding="async"
      />
      <div className="loading-rule" />
      <div className="loading-sub">The New Standard</div>
      <div
        className="loading-bar"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(progress * 100)}
      >
        <div className="loading-bar__fill" style={{ width: `${progress * 100}%` }} />
      </div>
    </div>
  );
}
